import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { commerceConfig } from "../config/commerce";

export interface CartItem {
  id: string | number;
  productId?: string | number;
  variationId?: string | number;
  handle: string;
  title: string;
  variantTitle?: string;
  sku?: string;
  price: number;
  regularPrice?: number;
  image: string;
  qty: number;
  attributes?: Record<string, string>;
}

export interface AddToCartInput {
  id: string | number;
  productId?: string | number;
  variationId?: string | number;
  handle: string;
  title: string;
  variantTitle?: string;
  sku?: string;
  price: number;
  regularPrice?: number;
  image: string;
  attributes?: Record<string, string>;
}

interface CartContextType {
  items: CartItem[];
  setItems: React.Dispatch<React.SetStateAction<CartItem[]>>;
  add: (product: AddToCartInput, qty?: number) => void;
  remove: (id: string | number) => void;
  updateQty: (id: string | number, qty: number) => void;
  clearCart: () => void;
  count: number;
  subtotal: number;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const CartContext = createContext<CartContextType | null>(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const parsed = JSON.parse(localStorage.getItem("meoora_cart") || "[]");
      if (Array.isArray(parsed)) {
        return parsed.map((item: any) => ({
          ...item,
          qty: Math.max(1, Number(item.qty || item.quantity || 1)),
          price: Number(item.price) || 0,
        }));
      }
      return [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  // Sync to localStorage and database
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("meoora_cart", JSON.stringify(items));

      // If user is logged in, sync cart to WordPress / WooCommerce DB
      const token = localStorage.getItem("meoora_auth_token");
      if (token) {
        const timeout = setTimeout(() => {
          fetch(`${commerceConfig.apiUrl}/user/sync`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ cart: items }),
          }).catch(() => {
            // Ignore background sync errors
          });
        }, 300);
        return () => clearTimeout(timeout);
      }
    }
  }, [items]);

  // Listen for external updates (e.g. login/signup guest merge)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorageUpdate = () => {
      try {
        const updated = JSON.parse(localStorage.getItem("meoora_cart") || "[]");
        if (Array.isArray(updated)) {
          setItems(
            updated.map((item: any) => ({
              ...item,
              qty: Math.max(1, Number(item.qty || item.quantity || 1)),
              price: Number(item.price) || 0,
            }))
          );
        }
      } catch {
        // Ignore
      }
    };
    window.addEventListener("meoora_cart_updated", handleStorageUpdate);
    return () => window.removeEventListener("meoora_cart_updated", handleStorageUpdate);
  }, []);

  const add = (product: AddToCartInput, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => String(i.id) === String(product.id));
      if (found) {
        return prev.map((i) =>
          String(i.id) === String(product.id) ? { ...i, qty: (Number(i.qty) || 1) + qty } : i
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          productId: product.productId,
          variationId: product.variationId,
          handle: product.handle,
          title: product.title,
          variantTitle: product.variantTitle,
          sku: product.sku,
          price: Number(product.price) || 0,
          regularPrice: product.regularPrice,
          image: product.image,
          attributes: product.attributes,
          qty: Math.max(1, qty),
        },
      ];
    });
    setOpen(true);
  };

  const remove = (id: string | number) => {
    setItems((prev) => prev.filter((i) => String(i.id) !== String(id)));
  };

  const updateQty = (id: string | number, qty: number) => {
    setItems((prev) =>
      prev.map((i) => (String(i.id) === String(id) ? { ...i, qty: Math.max(1, qty) } : i))
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const count = useMemo(() => items.reduce((s, i) => s + (Number(i.qty) || 1), 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + (Number(i.qty) || 1) * (Number(i.price) || 0), 0), [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        setItems,
        add,
        remove,
        updateQty,
        clearCart,
        count,
        subtotal,
        open,
        setOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

